import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { FavoritesResponseSchema, favoriteTabOf, type FavoriteEntity, type FavoriteWire, type FavoritesResponse } from '@shared/flow/signal-chips';

export const FAVORITES_QUERY_KEY = ['flow-favorites'] as const;

export function favoritesQueryKey(viewAs: string | null) {
  return [...FAVORITES_QUERY_KEY, viewAs ?? null] as const;
}

/**
 * Every tab's favorites (global then personal items, one list). Cached indefinitely and invalidated on every
 * favorite / saved-view mutation. Same sanctioned F11 exception as `useViews.ts`: an admin's edit reaches
 * teammates with no push channel, so focus refetches this query (`'always'`, because `staleTime: Infinity` is
 * never stale and plain `true` would no-op).
 */
export function useFavorites(viewAs: string | null) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: favoritesQueryKey(viewAs),
    staleTime: Infinity,
    refetchOnWindowFocus: 'always' as const,
    queryFn: () =>
      fetchJson(
        apiFetch,
        FavoritesResponseSchema,
        viewAs ? `/api/flow/favorites?viewAs=${encodeURIComponent(viewAs)}` : '/api/flow/favorites',
      ),
  });
}

/** The favorites for one tab, in server order (global first, then personal). */
export function favoritesForEntity(data: FavoritesResponse | undefined, entity: FavoriteEntity): FavoriteWire[] {
  return data ? data.items.filter(item => favoriteTabOf(item) === entity) : [];
}
